import { useEffect, useState } from 'react';
import { Loader2, FlaskConical, Send, RefreshCw, Smartphone, MessageSquare } from 'lucide-react';
import { api } from '../lib/spizirnaUi';

/**
 * Správa → Integrace → SMS (dřív Nastavení → SMS).
 *
 * Dokud není napojená brána, běží SMS v testovacím režimu: systém je
 * vytváří úplně stejně jako naostro (nezastižen, doklad SMS…), jen je
 * neodešle. Tady se dají přečíst — komu by šly, co v nich je a kolik
 * SMS by brána účtovala. Diakritika přepne zprávu na 70 znaků na SMS,
 * proto se délka ukazuje u každé.
 */
const GSM = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_ROZSIRENE = '^{}\\[~]|€';

/** Stejný výpočet jako SmsOdchozi::delka na serveru. */
export function delkaSms(text) {
    const znaky = [...text];
    let jednotek = 0;
    for (const z of znaky) {
        if (GSM_ROZSIRENE.includes(z)) jednotek += 2;
        else if (GSM.includes(z)) jednotek += 1;
        else {
            const n = znaky.length;
            return { kodovani: 'ucs2', znaku: n, casti: n <= 70 ? 1 : Math.ceil(n / 67), naCast: n <= 70 ? 70 : 67 };
        }
    }
    return { kodovani: 'gsm', znaku: jednotek, casti: jednotek <= 160 ? 1 : Math.ceil(jednotek / 153), naCast: jednotek <= 160 ? 160 : 153 };
}

const POPIS_TYPU = {
    nezastizen: 'Nezastižen', uctenka_sms: 'Doklad SMS', zkouska: 'Zkouška',
    prvni_sms: 'Potvrzení objednávky', za_rohem: 'Rozvozce se blíží', potvrzeni_objednavky: 'Potvrzení z hovoru',
};
const POPIS_STAVU = {
    neodeslano_test: { text: 'TEST · neodesláno', barva: '#F59E0B' },
    ve_fronte: { text: 'čeká na telefon', barva: '#3b82f6' },
    odeslano: { text: 'odesláno', barva: '#10b981' },
    doruceno: { text: 'doručeno', barva: '#10b981' },
    chyba: { text: 'chyba', barva: '#EF4444' },
};

export default function SmsPrehled({ isDark }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState('');
    const [telefon, setTelefon] = useState('');
    const [zprava, setZprava] = useState('FofrLama: Vaše objednávka je na cestě, rozvozce dorazí za 10 min.');
    const [odesila, setOdesila] = useState(false);

    const nacti = () => api('/api/sms/odchozi').then(setData).catch(() => setChyba('Přehled SMS se nepodařilo načíst.'));
    useEffect(() => { nacti(); }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : '#fff';
    const plocha = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';

    const delka = delkaSms(zprava);

    const zkouska = async () => {
        setChyba('');
        setOdesila(true);
        try {
            await api('/api/sms/zkouska', 'POST', { telefon, zprava });
            await nacti();
        } catch (e) {
            setChyba(e?.message || 'Zkušební SMS se nepodařilo vytvořit.');
        } finally {
            setOdesila(false);
        }
    };

    if (!data) {
        return chyba
            ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
            : <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;
    }

    const test = data.rezim !== 'ostry';

    return (
        <div className="space-y-4 max-w-4xl">
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>SMS</h3>
                <p className="text-xs mt-1" style={{ color: muted }}>
                    Všechny SMS, které systém vytvořil — zákazníkům při nezastižení, doklady a zkoušky. Odesílatel „{data.odesilatel}“.
                </p>
            </div>

            <div className="flex items-start gap-3 rounded-xl px-4 py-3"
                style={test
                    ? { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.4)' }
                    : { background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.4)' }}>
                {test
                    ? <FlaskConical size={18} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }} />
                    : <Smartphone size={18} style={{ color: '#10b981', flexShrink: 0, marginTop: 1 }} />}
                <div className="flex-1 text-xs" style={{ color: 'var(--text-primary)' }}>
                    {test
                        ? <><b>Testovací režim.</b> SMS se vytvářejí a ukládají sem, ale nikomu se neodesílají.</>
                        : <><b>Ostrý provoz přes telefon bistra.</b> SMS odchází z čísla telefonu{data.telefon ? ` „${data.telefon.nazev}“` : ''}
                            {data.telefon && !data.telefon.online && <b style={{ color: '#EF4444' }}> — telefon je teď offline, zprávy čekají ve frontě</b>}.</>}
                    <div className="mt-1" style={{ color: muted }}>
                        {data.telefon
                            ? 'Telefon s aplikací FofrTelefon odesílá SMS i hlásí příchozí.'
                            : 'Pro ostrý provoz spáruj telefon bistra v Správa → Provozovna → Pokladny a telefon.'}
                    </div>
                </div>
                <button onClick={async () => {
                    setChyba('');
                    try { await api('/api/sms/nastaveni', 'PUT', { rezim: test ? 'ostry' : 'test' }); await nacti(); }
                    catch (e) { setChyba(e?.error || e?.message || 'Přepnout se nepodařilo.'); }
                }}
                    className="text-xs font-semibold rounded-lg px-3 py-1.5 flex-shrink-0"
                    style={{ background: test ? '#10b981' : 'transparent', color: test ? '#fff' : muted,
                        border: `1px solid ${test ? '#10b981' : border}`, cursor: 'pointer' }}>
                    {test ? 'Posílat přes telefon' : 'Zpět na test'}
                </button>
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}

            <section className="rounded-xl px-4 py-3 space-y-2" style={{ background: plocha, border: `1px solid ${border}` }}>
                <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em' }}>Zkušební SMS</h4>
                <div className="flex flex-wrap gap-2">
                    <input value={telefon} onChange={e => setTelefon(e.target.value)} placeholder="+420 777 123 456"
                        className="text-sm rounded-lg px-3 py-2" style={{ width: 180, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                    <textarea value={zprava} onChange={e => setZprava(e.target.value)} rows={2}
                        className="flex-1 text-sm rounded-lg px-3 py-2"
                        style={{ minWidth: 260, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', resize: 'vertical' }} />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs" style={{ color: delka.casti > 1 ? '#F59E0B' : muted }}>
                        {delka.znaku} znaků · {delka.casti} SMS{delka.casti > 1 ? ' (vícedílná)' : ''}
                        {delka.kodovani === 'ucs2' && ' · s diakritikou jen 70 znaků na SMS'}
                    </span>
                    <button onClick={zkouska} disabled={!telefon.trim() || !zprava.trim() || odesila}
                        className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                        style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', color: '#F59E0B', cursor: 'pointer', opacity: !telefon.trim() || odesila ? 0.5 : 1 }}>
                        {odesila ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        {test ? 'Vytvořit (neodešle se)' : 'Odeslat'}
                    </button>
                </div>
            </section>

            {data.prichozi?.length > 0 && (
                <section>
                    <h4 className="text-xs font-bold uppercase mb-2" style={{ color: muted, letterSpacing: '0.06em' }}>
                        Příchozí ({data.prichozi.length})
                    </h4>
                    <div className="space-y-2">
                        {data.prichozi.map(s => (
                            <div key={s.id} className="rounded-xl px-4 py-3" style={{ background: plocha, border: `1px solid ${border}` }}>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                    <MessageSquare size={13} style={{ color: '#a855f7' }} />
                                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{s.telefon}</span>
                                    <span style={{ color: muted }}>{new Date(s.prijato_v).toLocaleString('cs-CZ')}</span>
                                    <span className="ml-auto font-semibold" style={{ color: s.vyrizeno_v ? muted : '#a855f7' }}>
                                        {s.vyrizeno_v ? 'vyřízeno' : 'nevyřízeno'}
                                    </span>
                                </div>
                                <p className="text-sm mt-1.5 whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{s.zprava}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em' }}>
                        Posledních {data.zpravy.length} odchozích SMS
                    </h4>
                    <button onClick={nacti} title="Obnovit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}>
                        <RefreshCw size={14} />
                    </button>
                </div>
                {data.zpravy.length === 0 ? (
                    <p className="text-xs" style={{ color: muted }}>Zatím žádná SMS nevznikla.</p>
                ) : (
                    <div className="space-y-2">
                        {data.zpravy.map(s => {
                            const stav = POPIS_STAVU[s.stav] ?? { text: s.stav, barva: muted };
                            return (
                                <div key={s.id} className="rounded-xl px-4 py-3" style={{ background: plocha, border: `1px solid ${border}` }}>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{s.telefon}</span>
                                        <span style={{ color: muted }}>{POPIS_TYPU[s.typ] ?? s.typ ?? '—'}</span>
                                        {s.objednavka_id && <span style={{ color: muted }}>obj. #{s.objednavka_id}</span>}
                                        <span style={{ color: muted }}>{new Date(s.created_at).toLocaleString('cs-CZ')}</span>
                                        <span className="ml-auto font-semibold px-2 py-0.5 rounded"
                                            style={{ color: stav.barva, background: `${stav.barva}1f` }}>{stav.text}</span>
                                    </div>
                                    <p className="text-sm mt-1.5" style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{s.zprava}</p>
                                    <p className="text-[11px] mt-1" style={{ color: s.casti > 1 ? '#F59E0B' : muted }}>
                                        {s.znaku} znaků · {s.casti} SMS{s.kodovani === 'ucs2' ? ' · diakritika' : ''}
                                        {s.chyba ? ` · ${s.chyba}` : ''}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
